/**
 * Lift2Fit — the web mirror of the legal policy version.
 *
 * The CANONICAL constant lives in `apps/api/src/policy.ts`; this file mirrors it
 * so the signup form can name the version it is showing without a round trip.
 * The two MUST match: the API refuses a signup whose `policyVersion` is not the
 * version it is serving (422 `policy_version_mismatch`), so a drift is safe but
 * visible. `apps/api/test/consent.test.ts` asserts the two files agree — bump
 * both in the same commit when the text changes.
 *
 * The documents are static content in this bundle. They are readable signed out
 * by design: consent happens before an account exists, so the page must not sit
 * behind a login.
 */

/** Bump on every change to the policy text (mirrors apps/api/src/policy.ts). */
export const POLICY_VERSION = '1';

/** The date the current version takes effect (`YYYY-MM-DD`). */
export const POLICY_EFFECTIVE_DATE = '2026-10-04';

/** The exact wording of the signup checkbox; one source so it cannot drift. */
export const CONSENT_STATEMENT =
  'I have read and understand the Privacy Policy, Terms and Health Disclaimer.';

/** A second line under the checkbox, spelling out what it does and does not cover. */
export const CONSENT_SUBLINE =
  'A single tick covers all three documents. It is not pre-ticked, and it is recorded against this policy version when your account is created. Untick it and no account is created.';

export type LegalDocId = 'privacy_policy' | 'terms_of_service' | 'health_disclaimer';

export interface LegalDocMeta {
  id: LegalDocId;
  /** The route this document lives at. */
  path: string;
  /** The full title used on the page and in the checkbox links. */
  title: string;
  /** A short label for navigation. */
  nav: string;
  /** One honest sentence about what the reader will find there. */
  summary: string;
}

export const LEGAL_DOCS: Record<LegalDocId, LegalDocMeta> = {
  privacy_policy: {
    id: 'privacy_policy',
    path: '/legal/privacy',
    title: 'Privacy Policy',
    nav: 'Privacy',
    summary: 'What we collect, why, on what lawful basis, and the rights you have over it.',
  },
  terms_of_service: {
    id: 'terms_of_service',
    path: '/legal/terms',
    title: 'Terms of Service',
    nav: 'Terms',
    summary: 'The agreement between you and Lift2Fit, including the limits of what we promise.',
  },
  health_disclaimer: {
    id: 'health_disclaimer',
    path: '/legal/health-disclaimer',
    title: 'Health & Medical Disclaimer',
    nav: 'Health disclaimer',
    summary: 'We are not a healthcare provider. Read this before you train with us.',
  },
};

/** The order the checkbox names the documents (matches the API constant). */
export const LEGAL_DOC_ORDER: readonly LegalDocId[] = [
  'privacy_policy',
  'terms_of_service',
  'health_disclaimer',
];

/** The same order as the API stores in `user_policy_consents.documents`. */
export function documentIds(): LegalDocId[] {
  return [...LEGAL_DOC_ORDER];
}

/** Resolve a `/legal/<slug>` URL segment (`privacy`) to its document id. */
export function legalDocIdForSlug(slug: string | undefined): LegalDocId | null {
  if (!slug) return null;
  const meta = Object.values(LEGAL_DOCS).find((doc) => doc.path === `/legal/${slug}`);
  return meta ? meta.id : null;
}

/** `4 October 2026` — the human form of the effective date, in UTC. */
export function effectiveDateLabel(): string {
  const date = new Date(`${POLICY_EFFECTIVE_DATE}T00:00:00Z`);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** `4 Oct 2026` — the compact form used in the acceptance record. */
export function acceptedDateLabel(iso: string | null | undefined): string {
  if (!iso) return 'date not recorded';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'date not recorded';
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export const CONTACT_PRIVACY = 'privacy@lift2fit.com';
export const CONTACT_DPO = 'dpo@lift2fit.com';
export const CONTACT_SUPPORT = 'support@lift2fit.com';
export const BRAND_LINE = 'One stop for all your fitness needs.';
export const DOMAIN = 'lift2fit.com';
